import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Card,
  CardContent,
  Autocomplete
} from '@mui/material';
import {
  Delete,
  Print,
  Add as AddIcon,
  CloudUpload,
  Save,
  Cancel,
  Comment,
  Assessment,
  Email,
  Receipt,
  LocalShipping,
  AccountBalance,
  Refresh,
  Feedback,
  Search
} from '@mui/icons-material';
import { customerService, contactService, addressService } from '../services/customerService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../hooks/useAuth';
import CustomerCreditLedger from '../components/CustomerCreditLedger';
import { formatCurrency } from '../components/CurrencyTextField';
import { quoteService } from '../services/quoteService';
import { orderService } from '../services/orderService';
import { customerInvoiceService } from '../services/customerInvoiceService';
import CustomerContactsModal from '../components/CustomerContactsModal';
import AttachmentCategoryChips from '../components/AttachmentCategoryChips';
import CustomerAddressesModal from '../components/CustomerAddressesModal';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import { formatCustomerAddressType } from '../utils/formatCustomerAddressType';
import { DEFAULT_PAYMENT_TERMS } from '../constants/defaultPaymentTerms';
import { fetchPaymentTermsOptions } from '../services/paymentTermsService';
import { attachmentService } from '../services/attachmentService';
import {
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_LABEL,
} from '../constants/attachmentLimits';
import { format } from 'date-fns';

const emptyNewCustomer = () => ({
  company_name: '',
  short_name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  notes: '',
  packing_slip_notify_enabled: true,
  packing_slip_notify_recipients: '',
  tax_exempt: false
});

const buildPrefillCustomer = (source) => {
  if (!source) return {};
  return {
    company_name: source.company_name || '',
    short_name: source.short_name || '',
    first_name: source.first_name || '',
    last_name: source.last_name || '',
    email: source.email || '',
    phone: source.phone || '',
    address_line1: source.address_line1 || '',
    address_line2: source.address_line2 || '',
    city: source.city || '',
    state: source.state || '',
    zip_code: source.zip_code || '',
    country: source.country || '',
    notes: source.notes || '',
    website: source.website || '',
    fax: source.fax || '',
    sales_id: source.sales_id || source.salesman_id || '',
    work_code: source.work_code || '',
    sourced_by_employee_id: source.sourced_by_employee_id || null,
    account_rep_employee_id: source.account_rep_employee_id || null,
    sourced_by_first_name: source.sourced_by_first_name || '',
    sourced_by_last_name: source.sourced_by_last_name || '',
    account_rep_first_name: source.account_rep_first_name || '',
    account_rep_last_name: source.account_rep_last_name || '',
    terms: source.terms || 'Payable On Receipt',
    credit_limit: source.credit_limit ?? null,
    ytd_sales: source.ytd_sales ?? null,
    current_ar: source.current_ar ?? null,
    prepaid_balance: source.credit_available ?? source.prepaid_balance ?? 0,
    credit_available: source.credit_available ?? source.prepaid_balance ?? 0,
    date_opened: source.date_opened || null,
    last_activity: source.last_activity || null,
    avg_days_to_pay: source.avg_days_to_pay ?? null,
    packing_slip_notify_enabled: source.packing_slip_notify_enabled !== false,
    packing_slip_notify_recipients: source.packing_slip_notify_recipients || '',
    tax_exempt: source.tax_exempt === true
  };
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = id === 'new';
  /** Stable object so useEffect deps do not change every render (buildPrefillCustomer({}) returns a new {} each call). */
  const prefillCustomer = useMemo(
    () => buildPrefillCustomer(location.state?.prefillCustomer),
    [location.state?.prefillCustomer]
  );
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(1); // Credit tab is active by default
  const [editedCustomer, setEditedCustomer] = useState(isNew ? { ...emptyNewCustomer(), ...prefillCustomer } : null);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [hasRelatedRecords, setHasRelatedRecords] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [addressesModalOpen, setAddressesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState(DEFAULT_PAYMENT_TERMS);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [employees, setEmployees] = useState([]);
  const { isAdmin } = useAuth();

  useEffect(() => {
    let cancelled = false;
    employeeService.getAll({ limit: 500 }).then((res) => {
      if (cancelled) return;
      const list = res.data || res || [];
      setEmployees(Array.isArray(list) ? list.filter((e) => e.is_active !== false) : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentTermsOptions().then((list) => {
      if (!cancelled) setPaymentTermsOptions(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Existing customer: only re-fetch when id changes. Do not depend on prefill (unstable / N/A for this route).
  useEffect(() => {
    if (id && !isNew) {
      loadCustomer();
      loadContacts();
      loadAddresses();
      loadAttachments();
    }
  }, [id, isNew]);

  // New customer: reset form when landing on /customers/new or when navigation state changes (e.g. copy prefill).
  useEffect(() => {
    if (!isNew) return;
    setLoading(false);
    setEditedCustomer({ ...emptyNewCustomer(), ...prefillCustomer });
  }, [isNew, location.key, prefillCustomer]);

  useEffect(() => {
    if (!id || isNew) return undefined;
    const customerId = parseInt(id, 10);
    if (Number.isNaN(customerId)) return undefined;
    let cancelled = false;
    setDependencyLoading(true);
    setHasRelatedRecords(false);
    (async () => {
      try {
        const [quotesRes, ordersRes, invoicesRes] = await Promise.all([
          quoteService.getAll({ customerId, limit: 1 }),
          orderService.getAll({ customerId, limit: 1 }),
          customerInvoiceService.getAll({ customer_id: customerId, limit: 1 })
        ]);
        if (cancelled) return;
        const listFrom = (res) => (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
        const hasQuotes = listFrom(quotesRes).length > 0;
        const hasOrders = listFrom(ordersRes).length > 0;
        const hasInvoices = listFrom(invoicesRes).length > 0;
        setHasRelatedRecords(hasQuotes || hasOrders || hasInvoices);
      } catch (err) {
        console.error('Error loading customer dependencies:', err);
        if (!cancelled) setHasRelatedRecords(false);
      } finally {
        if (!cancelled) setDependencyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  const creditTermSelectOptions = useMemo(() => {
    const t = editedCustomer?.terms;
    if (t && !paymentTermsOptions.includes(t)) {
      return [...paymentTermsOptions, t];
    }
    return paymentTermsOptions;
  }, [paymentTermsOptions, editedCustomer?.terms]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await customerService.getById(id);
      setCustomer(response.data);
      setEditedCustomer(response.data);
    } catch (err) {
      console.error('Error loading customer:', err);
      setError(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await contactService.getByCustomerId(id);
      setContacts(response.data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const response = await addressService.getByCustomerId(id);
      setAddresses(response.data || []);
    } catch (err) {
      console.error('Error loading addresses:', err);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const loadAttachments = async () => {
    if (!id || isNew) return;
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForCustomer(id);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error('Error loading attachments:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!files || files.length === 0 || !id || isNew) return;
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }
    const tooLarge = files.filter((f) => f.size > MAX_ATTACHMENT_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      setAttachmentsError(`One or more files exceed the ${MAX_ATTACHMENT_FILE_SIZE_LABEL} size limit.`);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, customerId: id });
      }
      await loadAttachments();
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleAttachmentFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment?.id) return;
      setAttachmentsError('');
      const res = await attachmentService.download(attachment.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || `attachment-${attachment.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  useEffect(() => {
    if (activeTab !== 5 || isNew) return;
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, [activeTab, isNew]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/customers');
      return;
    }
    setEditedCustomer(customer);
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const confirmDeleteCustomer = async () => {
    if (!id || isNew) return;
    try {
      setDeleting(true);
      setError('');
      await customerService.delete(id);
      setDeleteDialogOpen(false);
      navigate('/customers');
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(err.response?.data?.message || 'Failed to delete customer');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const effectivePackingSlipRecipients = () => {
    if (!editedCustomer) return '';
    if (editedCustomer.packing_slip_notify_recipients && editedCustomer.packing_slip_notify_recipients.trim()) {
      return editedCustomer.packing_slip_notify_recipients.trim();
    }
    return '(uses system default list)';
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      if (isNew) {
        if (!editedCustomer.company_name && !editedCustomer.first_name) {
          setError('Either company name or first name is required.');
          setLoading(false);
          return;
        }
        const res = await customerService.create(editedCustomer);
        const newId = res?.data?.id ?? res?.id;
        if (newId) navigate(`/customers/${newId}`);
        else navigate('/customers');
      } else {
        await customerService.update(id, editedCustomer);
        await loadCustomer();
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.response?.data?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  if (loading && !customer && !isNew) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !customer && !isNew) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/customers')} sx={{ mt: 2 }}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  // New customer form
  if (isNew) {
    const d = editedCustomer || emptyNewCustomer();
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            New Customer
          </Typography>
          <Box>
            <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" startIcon={<Cancel />} onClick={handleCancel} sx={{ ml: 1 }}>
              Cancel
            </Button>
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Paper sx={{ p: 2, maxWidth: 640 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
            General
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Customer" value={d.company_name || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, company_name: e.target.value })} />
              <TextField
                fullWidth
                label="Short name"
                value={d.short_name || ''}
                size="small"
                sx={{ mb: 1.5 }}
                placeholder="Optional abbreviated name for lists"
                onChange={(e) => setEditedCustomer({ ...d, short_name: e.target.value })}
              />
              <TextField fullWidth label="First name" value={d.first_name || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, first_name: e.target.value })} />
              <TextField fullWidth label="Last name" value={d.last_name || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, last_name: e.target.value })} />
              <TextField fullWidth label="Email" value={d.email || ''} size="small" type="email" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, email: e.target.value })} />
              <TextField fullWidth label="Phone" value={d.phone || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Address line 1" value={d.address_line1 || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, address_line1: e.target.value })} />
              <TextField fullWidth label="Address line 2" value={d.address_line2 || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, address_line2: e.target.value })} />
              <TextField fullWidth label="City" value={d.city || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, city: e.target.value })} />
              <TextField fullWidth label="State" value={d.state || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, state: e.target.value })} />
              <TextField fullWidth label="Zip code" value={d.zip_code || ''} size="small" sx={{ mb: 1.5 }}
                onChange={(e) => setEditedCustomer({ ...d, zip_code: e.target.value })} />
              <TextField fullWidth label="Notes" value={d.notes || ''} size="small" multiline rows={2}
                onChange={(e) => setEditedCustomer({ ...d, notes: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  if (!customer || !editedCustomer) {
    return null;
  }

  const customerData = editedCustomer;
  const deleteDisabled = dependencyLoading || hasRelatedRecords;
  const deleteTitle = dependencyLoading
    ? 'Checking related records…'
    : hasRelatedRecords
      ? 'Cannot delete: this customer has quotes, orders, or invoices'
      : 'Delete';

  return (
    <Box>
      <Box className="no-print-customer-detail">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customer Detail
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" color="success" title="Save" onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : <Save fontSize="small" />}
          </IconButton>
          <IconButton size="small" color="default" title="Revert unsaved changes" onClick={handleCancel}>
            <Cancel fontSize="small" />
          </IconButton>
          <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }} title={deleteTitle}>
            <IconButton
              size="small"
              color="error"
              onClick={handleDeleteClick}
              disabled={deleteDisabled}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
          <IconButton
            size="small"
            color="primary"
            title="Print"
            onClick={() => printWithFilename(customer?.customer_number || customer?.id ? `Customer-${customer?.customer_number || customer?.id}` : 'Customer')}
          >
            <Print fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Tabs */}
          <Paper sx={{ mb: 1.5 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Credit" />
              <Tab label={`Contacts ${contacts.length > 0 ? `(${contacts.length})` : ''}`} />
              <Tab label={`Addresses ${addresses.length > 0 ? `(${addresses.length})` : ''}`} />
              <Tab label="Comments" />
              <Tab label="User Defined" />
              <Tab label={attachments.length > 0 ? `Attachments (${attachments.length})` : 'Attachments'} />
            </Tabs>
          </Paper>

          {/* General Section */}
          <Paper sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
              General
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Code"
                  value={customerData.customer_number || ''}
                  disabled
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Customer"
                  value={customerData.company_name || `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() || ''}
                  size="small"
                  helperText="Full company or primary contact name"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, company_name: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Short name"
                  value={customerData.short_name || ''}
                  size="small"
                  placeholder="Optional abbreviated name for lists"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, short_name: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Web Site"
                  value={customerData.website || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, website: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={customerData.phone || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, phone: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="FAX"
                  value={customerData.fax || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, fax: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Sales ID (legacy)"
                  value={customerData.sales_id || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, sales_id: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <Autocomplete
                  size="small"
                  options={employees}
                  getOptionLabel={(e) => (e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : '')}
                  value={employees.find((e) => e.id === customerData.account_rep_employee_id) || null}
                  onChange={(_, val) => setEditedCustomer({
                    ...editedCustomer,
                    account_rep_employee_id: val?.id || null,
                  })}
                  renderInput={(params) => (
                    <TextField {...params} label="Account Rep" sx={{ mb: 1.5 }} />
                  )}
                />
                <TextField
                  fullWidth
                  label="Sourced By"
                  size="small"
                  value={(() => {
                    if (!customerData.sourced_by_employee_id) return '—';
                    const fromCustomer = `${customerData.sourced_by_first_name || ''} ${customerData.sourced_by_last_name || ''}`.trim();
                    if (fromCustomer) return fromCustomer;
                    const emp = employees.find((e) => e.id === customerData.sourced_by_employee_id);
                    return emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : '—';
                  })()}
                  InputProps={{ readOnly: true }}
                  helperText={isAdmin() ? 'Permanent credit; use admin edit below to change' : 'Permanent credit for who brought this customer in'}
                  sx={{ mb: 1.5 }}
                />
                {isAdmin() && (
                  <Autocomplete
                    size="small"
                    options={employees}
                    getOptionLabel={(e) => (e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : '')}
                    value={employees.find((e) => e.id === customerData.sourced_by_employee_id) || null}
                    onChange={(_, val) => setEditedCustomer({
                      ...editedCustomer,
                      sourced_by_employee_id: val?.id || null,
                      sourced_by_first_name: val?.first_name || '',
                      sourced_by_last_name: val?.last_name || '',
                    })}
                    renderInput={(params) => (
                      <TextField {...params} label="Sourced By (admin edit)" sx={{ mb: 1.5 }} />
                    )}
                  />
                )}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Packing slip notifications
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={customerData.packing_slip_notify_enabled !== false}
                      onChange={(e) =>
                        setEditedCustomer({
                          ...editedCustomer,
                          packing_slip_notify_enabled: e.target.checked
                        })}
                      size="small"
                    />
                  )}
                  label="Enable email notifications for packing slips"
                />
                <TextField
                  fullWidth
                  label="Override recipient emails (comma-separated)"
                  value={customerData.packing_slip_notify_recipients || ''}
                  size="small"
                  onChange={(e) =>
                    setEditedCustomer({
                      ...editedCustomer,
                      packing_slip_notify_recipients: e.target.value
                    })}
                  sx={{ mt: 1 }}
                  helperText={effectivePackingSlipRecipients()}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address line 1"
                  value={customerData.address_line1 || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, address_line1: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Address line 2"
                  value={customerData.address_line2 || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, address_line2: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="City"
                  value={customerData.city || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, city: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="State"
                  value={customerData.state || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, state: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="Zip code"
                  value={customerData.zip_code || ''}
                  size="small"
                  onChange={(e) => setEditedCustomer({ ...editedCustomer, zip_code: e.target.value })}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Box>
          </Paper>

      {/* Tab Content */}
          {activeTab === 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                Credit
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Credit Limit"
                    value={customerData.credit_limit ?? ''}
                    size="small"
                    type="number"
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, credit_limit: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Credit Status"
                    value="Open Account"
                    disabled
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="YTD Sales"
                    value={customerData.ytd_sales ?? ''}
                    size="small"
                    type="number"
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, ytd_sales: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Current A/R"
                    value={customerData.current_ar ?? ''}
                    size="small"
                    type="number"
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, current_ar: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Credit on account (unapplied payments)"
                    value={formatCurrency(customerData.credit_available ?? customerData.prepaid_balance ?? 0, '$0.00')}
                    size="small"
                    InputProps={{ readOnly: true }}
                    helperText="Equals sum of unapplied balances on payments below"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date Opened"
                    value={formatDateForInput(customerData.date_opened)}
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, date_opened: e.target.value || null })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Last Activity"
                    value={formatDateForInput(customerData.last_activity || customerData.updated_at)}
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, last_activity: e.target.value || null })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    label="Avg Days to Pay"
                    value={customerData.avg_days_to_pay ?? ''}
                    size="small"
                    type="number"
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, avg_days_to_pay: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    select
                    label="Terms"
                    value={customerData.terms || 'Payable On Receipt'}
                    size="small"
                    onChange={(e) => setEditedCustomer({ ...editedCustomer, terms: e.target.value })}
                    sx={{ mb: 1.5 }}
                  >
                    {creditTermSelectOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={customerData.tax_exempt === true}
                        onChange={(e) =>
                          setEditedCustomer({ ...editedCustomer, tax_exempt: e.target.checked })
                        }
                      />
                    )}
                    label="Tax exempt"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
                    When checked, this customer is flagged as exempt from sales tax (use for quotes and invoices as your process allows).
                  </Typography>
                </Grid>
              </Grid>
              <CustomerCreditLedger customerId={id} />
            </Paper>
          )}

          {activeTab === 1 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Contacts
                </Typography>
                <Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setContactsModalOpen(true)}
                    sx={{ mr: 0.5 }}
                  >
                    Manage Contacts
                  </Button>
                  <IconButton size="small" color="primary" title="Comments">
                    <Comment fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="primary" title="Generate Report">
                    <Assessment fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="primary" title="Email">
                    <Email fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              {loadingContacts ? (
                <CircularProgress />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Notes</TableCell>
                        <TableCell>A/P</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Ext</TableCell>
                        <TableCell>Cell Phone Number</TableCell>
                        <TableCell>FAX Number</TableCell>
                        <TableCell>EMail Address</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            No contacts found
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell>{contact.notes || '-'}</TableCell>
                            <TableCell>
                              <Checkbox checked={contact.is_ap_contact || false} disabled size="small" />
                            </TableCell>
                            <TableCell>
                              <Checkbox checked={contact.is_active !== false} disabled size="small" />
                            </TableCell>
                            <TableCell>{contact.name || '-'}</TableCell>
                            <TableCell>{contact.title || '-'}</TableCell>
                            <TableCell>{contact.phone_number || '-'}</TableCell>
                            <TableCell>{contact.ext || '-'}</TableCell>
                            <TableCell>{contact.cell_phone_number || '-'}</TableCell>
                            <TableCell>{contact.fax_number || '-'}</TableCell>
                            <TableCell>{contact.email_address || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {activeTab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Addresses
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddressesModalOpen(true)}
                >
                  Manage Addresses
                </Button>
              </Box>
              {loadingAddresses ? (
                <CircularProgress />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Address Line 1</TableCell>
                        <TableCell>Address Line 2</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Zip Code</TableCell>
                        <TableCell>Country</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {addresses.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No addresses found
                          </TableCell>
                        </TableRow>
                      ) : (
                        addresses.map((address) => (
                          <TableRow key={address.id}>
                            <TableCell>{formatCustomerAddressType(address.address_type)}</TableCell>
                            <TableCell>{address.address_line1 || '-'}</TableCell>
                            <TableCell>{address.address_line2 || '-'}</TableCell>
                            <TableCell>{address.city || '-'}</TableCell>
                            <TableCell>{address.state || '-'}</TableCell>
                            <TableCell>{address.zip_code || '-'}</TableCell>
                            <TableCell>{address.country || 'USA'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {activeTab === 3 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                Comments
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={customerData.notes || ''}
                onChange={(e) => setEditedCustomer({ ...editedCustomer, notes: e.target.value })}
              />
            </Paper>
          )}

          {activeTab === 4 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                User Defined
              </Typography>
              <Typography variant="body2" color="textSecondary">
                User-defined fields coming soon
              </Typography>
            </Paper>
          )}

          {activeTab === 5 && (
            <Card sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                  Attachments
                </Typography>
                {isNew ? (
                  <Typography variant="body2" color="text.secondary">
                    Save the customer first, then you can add attachments here (same as quotes and orders: up to 5 files
                    at a time, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each).
                  </Typography>
                ) : (
                  <>
                    <Box
                      sx={{
                        mb: 1.5,
                        minHeight: 120,
                        p: 2,
                        border: '2px dashed',
                        borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
                        borderRadius: 2,
                        bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        transition: 'border-color 0.15s ease, background-color 0.15s ease'
                      }}
                      onDragOver={handleAttachmentDragOver}
                      onDragLeave={handleAttachmentDragLeave}
                      onDrop={handleAttachmentDrop}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CloudUpload sx={{ fontSize: 40, opacity: isDraggingOverAttachment ? 1 : 0.6 }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Drop files here
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            or use the Upload button · max 5 files, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Attachments: {attachments.length}
                          </Typography>
                        </Box>
                      </Box>
                      <Button variant="contained" component="label" size="small" startIcon={<AddIcon />}>
                        Upload
                        <input
                          type="file"
                          hidden
                          multiple
                          onChange={handleAttachmentFileInputChange}
                        />
                      </Button>
                    </Box>
                    {attachmentsError && (
                      <Alert severity="error" sx={{ mb: 1 }} onClose={() => setAttachmentsError('')}>
                        {attachmentsError}
                      </Alert>
                    )}
                    {attachmentsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : attachments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No attachments yet. Drag and drop files onto the area above or use the Upload button.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>File Name</TableCell>
                              <TableCell>Size</TableCell>
                              <TableCell>Uploaded At</TableCell>
                              <TableCell>Categories</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {attachments.map((att) => (
                              <TableRow key={att.id} hover>
                                <TableCell>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadAttachment(att);
                                    }}
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {att.file_name}
                                  </Button>
                                </TableCell>
                                <TableCell>
                                  {att.file_size != null
                                    ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB`
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  {att.uploaded_at
                                    ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm')
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  <AttachmentCategoryChips
                                    attachmentId={att.id}
                                    categories={att.categories}
                                    onUpdated={(row) =>
                                      setAttachments((prev) =>
                                        prev.map((a) => (a.id === row.id ? { ...a, ...row } : a))
                                      )
                                    }
                                    onError={(msg) => setAttachmentsError(msg)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteAttachment(att.id)}
                                    aria-label="Delete attachment"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Customer Inquiry Details - Quick Links */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: 'surfaceAccent.main' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Customer Inquiry Details
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Search />}
                  sx={{ mb: 1 }}
                  onClick={() => navigate(`/quotes?customer=${customer.id}`)}
                >
                  Quotes
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Receipt />}
                  sx={{ mb: 1 }}
                >
                  Sales
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Assessment />}
                  sx={{ mb: 1 }}
                  onClick={() => navigate(`/work-orders?customer=${customer.id}`)}
                >
                  Orders
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Receipt />}
                  sx={{ mb: 1 }}
                >
                  Receipts
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<LocalShipping />}
                  sx={{ mb: 1 }}
                >
                  Shipments
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<AccountBalance />}
                  sx={{ mb: 1 }}
                >
                  Receivables
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Refresh />}
                  sx={{ mb: 1 }}
                >
                  Returns & Rework
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Feedback />}
                  sx={{ mb: 1 }}
                >
                  Feedback
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      </Box>

      {/* Print view – customer summary */}
      {customer && (
        <PrintPage title="Customer">
          <div className="print-quote-details-box" style={{ marginBottom: 16 }}>
            <div className="print-quote-details-left">
              <div>
                <strong>Customer Code:</strong> {customer.customer_number || customer.id}
              </div>
              <div>
                <strong>Customer:</strong>{' '}
                {customer.company_name ||
                  [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
                  '—'}
              </div>
              {customer.short_name ? (
                <div>
                  <strong>Short name:</strong> {customer.short_name}
                </div>
              ) : null}
              <div>
                <strong>Phone:</strong> {customer.phone || '—'}
              </div>
              <div>
                <strong>Email:</strong> {customer.email || '—'}
              </div>
            </div>
            <div className="print-quote-details-right">
              <div>
                <strong>Address:</strong>{' '}
                {[customer.address_line1, customer.address_line2].filter(Boolean).join(', ') ||
                  '—'}
              </div>
              {(customer.city || customer.state || customer.zip_code) && (
                <div>
                  {[customer.city, [customer.state, customer.zip_code].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 12, fontSize: 12 }}>
            <strong>Credit summary</strong>
          </div>
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
            <tbody>
              <tr>
                <td style={{ padding: 6, width: '40%' }}>Credit limit</td>
                <td style={{ padding: 6 }}>{customer.credit_limit ?? '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>YTD sales</td>
                <td style={{ padding: 6 }}>{customer.ytd_sales ?? '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>Current A/R</td>
                <td style={{ padding: 6 }}>{customer.current_ar ?? '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>Credit on account</td>
                <td style={{ padding: 6 }}>{customer.prepaid_balance != null ? customer.prepaid_balance : '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>Tax exempt</td>
                <td style={{ padding: 6 }}>{customer.tax_exempt ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>Date opened</td>
                <td style={{ padding: 6 }}>{formatDateForInput(customer.date_opened) || '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: 6 }}>Last activity</td>
                <td style={{ padding: 6 }}>
                  {formatDateForInput(customer.last_activity || customer.updated_at) || '—'}
                </td>
              </tr>
            </tbody>
          </table>

          {(contacts?.length ?? 0) > 0 && (
            <>
              <div style={{ marginBottom: 8, fontSize: 12 }}>
                <strong>Contacts</strong>
              </div>
              <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 6, textAlign: 'left' }}>Name</th>
                    <th style={{ padding: 6, textAlign: 'left' }}>Email</th>
                    <th style={{ padding: 6, textAlign: 'left' }}>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id}>
                      <td style={{ padding: 6 }}>
                        {c.name ||
                          [c.first_name, c.last_name].filter(Boolean).join(' ') ||
                          '—'}
                      </td>
                      <td style={{ padding: 6 }}>{c.email || '—'}</td>
                      <td style={{ padding: 6 }}>{c.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {(addresses?.length ?? 0) > 0 && (
            <>
              <div style={{ marginBottom: 8, fontSize: 12 }}>
                <strong>Other addresses</strong>
              </div>
              <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 6, textAlign: 'left' }}>Label</th>
                    <th style={{ padding: 6, textAlign: 'left' }}>Address</th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.map((a) => (
                    <tr key={a.id}>
                      <td style={{ padding: 6 }}>{a.label || '—'}</td>
                      <td style={{ padding: 6 }}>
                        {[a.address_line1, a.address_line2].filter(Boolean).join(', ')}
                        {a.city || a.state || a.zip_code
                          ? ` — ${[a.city, [a.state, a.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ')}`
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </PrintPage>
      )}

      {/* Contacts Modal */}
      <CustomerContactsModal
        open={contactsModalOpen}
        onClose={() => { setContactsModalOpen(false); loadContacts(); }}
        customerId={id}
        customerName={(() => {
          const L =
            customer?.company_name ||
            `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
            '';
          const s = customer?.short_name?.trim();
          if (s && L) return `${s} (${L})`;
          return s || L || 'Customer';
        })()}
      />

      {/* Addresses Modal */}
      <CustomerAddressesModal
        open={addressesModalOpen}
        onClose={() => { setAddressesModalOpen(false); loadAddresses(); }}
        customerId={id}
        customerName={(() => {
          const L =
            customer?.company_name ||
            `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
            '';
          const s = customer?.short_name?.trim();
          if (s && L) return `${s} (${L})`;
          return s || L || 'Customer';
        })()}
      />

      {/* Delete Customer Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        aria-labelledby="delete-customer-dialog-title"
        aria-describedby="delete-customer-dialog-description"
      >
        <DialogTitle id="delete-customer-dialog-title">
          Delete Customer?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-customer-dialog-description">
            Are you sure you want to delete this customer? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteCustomer} color="error" variant="contained" autoFocus disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetail;
